import { useEffect, useState } from "react";
import { useParams } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Calendar, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { SiGoogle } from "react-icons/si";
import { apiRequest } from "@/lib/queryClient";

interface InviteStatus {
  valid: boolean;
  talentName?: string;
  memberName?: string;
  workspaceName?: string;
  expiresAt?: string;
  error?: string;
}

export default function ConnectCalendar() {
  const params = useParams<{ token: string }>();
  const token = params.token;
  const [isConnecting, setIsConnecting] = useState(false);

  const { data: inviteStatus, isLoading } = useQuery<InviteStatus>({
    queryKey: ["/api/public/calendar-invite", token],
    queryFn: async () => {
      const response = await fetch(`/api/public/calendar-invite/${token}`);
      if (!response.ok) {
        const data = await response.json();
        return { valid: false, error: data.message || "Invalid invitation" };
      }
      return response.json();
    },
    enabled: !!token,
  });

  const handleConnect = async () => {
    if (!token) return;
    setIsConnecting(true);
    
    // Redirect to the OAuth start endpoint
    window.location.href = `/api/public/calendar-invite/${token}/auth`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!inviteStatus?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
              <XCircle className="w-6 h-6 text-destructive" />
            </div>
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>
              {inviteStatus?.error || "This invitation link is no longer valid. It may have expired or already been used."}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground">
              Please contact your administrator for a new invitation.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Calendar className="w-6 h-6 text-primary" />
          </div>
          <CardTitle>Connect Your Calendar</CardTitle>
          <CardDescription>
            <span className="font-medium">{inviteStatus.workspaceName}</span> has invited you to connect your Google Calendar
            {inviteStatus.memberName ? (
              <> as <span className="font-medium">{inviteStatus.memberName}</span> of <span className="font-medium">{inviteStatus.talentName}</span>.</>
            ) : (
              <> for <span className="font-medium">{inviteStatus.talentName}</span>.</>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-2">What this allows:</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Check your availability for bookings</li>
              <li>Add confirmed shows to your calendar</li>
              <li>Prevent double-bookings</li>
            </ul>
          </div>

          {inviteStatus.expiresAt && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="w-4 h-4" />
              <span>Expires {new Date(inviteStatus.expiresAt).toLocaleDateString()}</span>
            </div>
          )}

          <Button 
            className="w-full" 
            size="lg"
            onClick={handleConnect}
            disabled={isConnecting}
            data-testid="button-connect-google-calendar"
          >
            {isConnecting ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <SiGoogle className="w-5 h-5 mr-2" />
            )}
            Connect with Google
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            By connecting, you authorize read and write access to your Google Calendar.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
